/**
 * Jyotish Tarang — Panchang + Ghata Chakra Tab v1.0 (v3.16)
 * 5 Panchang limbs computed from chart planets + Ghata Chakra grid.
 * No extra API call — all data from chart.planets and chart.meta.
 */
import React from 'react';

interface Props { chart?: any; }

// ── Lookup tables ─────────────────────────────────────────────────────────────
const VARA_NAMES   = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const VARA_LORDS   = ['Sun','Moon','Mars','Mercury','Jupiter','Venus','Saturn'];
const VARA_NATURE  = ['Fierce','Mild','Fierce','Neutral','Auspicious','Auspicious','Restrictive'];

const TITHI_NAMES = [
  'Pratipada','Dwitiya','Tritiya','Chaturthi','Panchami',
  'Shashthi','Saptami','Ashtami','Navami','Dashami',
  'Ekadashi','Dwadashi','Trayodashi','Chaturdashi','Purnima / Amavasya',
];
const TITHI_GOOD  = new Set([2,3,5,7,10,11,12,13,15]);
const TITHI_BAD   = new Set([4,6,8,9,14,30]);

const NAK_NAMES = [
  '','Ashwini','Bharani','Krittika','Rohini','Mrigashira','Ardra',
  'Punarvasu','Pushya','Ashlesha','Magha','Purva Phalguni','Uttara Phalguni',
  'Hasta','Chitra','Swati','Vishakha','Anuradha','Jyeshtha','Mula',
  'Purva Ashadha','Uttara Ashadha','Shravana','Dhanishtha','Shatabhisha',
  'Purva Bhadrapada','Uttara Bhadrapada','Revati',
];
const NAK_LORDS = ['','Ketu','Venus','Sun','Moon','Mars','Rahu','Jupiter',
                   'Saturn','Mercury','Ketu','Venus','Sun','Moon','Mars','Rahu',
                   'Jupiter','Saturn','Mercury','Ketu','Venus','Sun','Moon',
                   'Mars','Rahu','Jupiter','Saturn','Mercury'];

const YOGA_NAMES = [
  '','Vishkambha','Priti','Ayushman','Saubhagya','Shobhana','Atiganda',
  'Sukarma','Dhriti','Shula','Ganda','Vriddhi','Dhruva','Vyaghata',
  'Harshana','Vajra','Siddhi','Vyatipata','Variyan','Parigha','Shiva',
  'Siddha','Sadhya','Shubha','Shukla','Brahma','Indra','Vaidhriti',
];
const YOGA_BAD = new Set([1,6,9,10,13,15,17,19,27]);

const KARANA_MOV = ['Bava','Balava','Kaulava','Taitila','Garaja','Vanija','Vishti(Bhadra)'];
const KARANA_BAD = new Set(['Vishti(Bhadra)']);

// Ghata Chakra: starting nak per weekday (0=Sun), pattern +5 each day, +9 within day
// Sunday starts at nak 4 (Rohini), each weekday +5, each set +9
function ghataForVara(vara: number): number[] {
  const base = ((3 + 5 * vara) % 27) + 1;
  return [
    base,
    ((base - 1 + 9)  % 27) + 1,
    ((base - 1 + 18) % 27) + 1,
  ];
}

// ── Computation from chart data ───────────────────────────────────────────────
// Exported (Phase 3, 10 Jul 2026) so the "Today" dashboard can render a
// condensed birth-Panchang widget without duplicating this math. The
// server-side port lives in backend/utils/panchang_engine.py::compute_panchang_limbs
// (used for the PDF) — keep both in sync if this changes.
export function computePanchang(chart: any) {
  const sunLon  = chart?.planets?.RAVI?.degree    as number ?? 0;
  const moonLon = chart?.planets?.CHANDRA?.degree as number ?? 0;
  const jd      = chart?.meta?.jd                as number ?? 0;

  const diff = (moonLon - sunLon + 360) % 360;

  // Vara (weekday)
  const vara = Math.floor((jd + 1.5)) % 7;   // 0=Sun

  // Tithi (1-30)
  const tithiNum = Math.floor(diff / 12) + 1;
  const tithiIdx = ((tithiNum - 1) % 15);
  const paksha   = tithiNum <= 15 ? 'Shukla (Waxing)' : 'Krishna (Waning)';

  // Nakshatra of Moon (1-27)
  const nakNum = Math.floor(moonLon / (360 / 27)) % 27 + 1;

  // Yoga (1-27)
  const yogaNum = Math.floor(((sunLon + moonLon) % 360) / (360 / 27)) % 27 + 1;

  // Karana (1-60 position in lunar month)
  const karanaPos = Math.floor(diff / 6);
  let karanaName: string;
  if (karanaPos === 0)       karanaName = 'Kimstughna';
  else if (karanaPos === 57) karanaName = 'Shakuni';
  else if (karanaPos === 58) karanaName = 'Chatushpada';
  else if (karanaPos === 59) karanaName = 'Naga';
  else                       karanaName = KARANA_MOV[(karanaPos - 1) % 7];

  return { vara, tithiNum, tithiIdx, paksha, nakNum, yogaNum, karanaName };
}

/** Human-readable labels version of computePanchang(), for compact
 * widgets (e.g. the "Today" dashboard) that don't need the raw indices. */
export function computePanchangLabels(chart: any) {
  if (!chart?.planets) return null;
  const p = computePanchang(chart);
  return {
    vara:      VARA_NAMES[p.vara],
    varaLord:  VARA_LORDS[p.vara],
    tithi:     TITHI_NAMES[p.tithiIdx],
    paksha:    p.paksha,
    nakshatra: NAK_NAMES[p.nakNum],
    yoga:      YOGA_NAMES[p.yogaNum],
    karana:    p.karanaName,
  };
}

// ── Shared sub-component ──────────────────────────────────────────────────────
function LimbCard({ icon, label, value, sub, badge, badgeColor }:
  { icon:string; label:string; value:string; sub?:string;
    badge?:string; badgeColor?:string }) {
  return (
    <div style={{ background:'var(--ivory)', border:'1px solid var(--parch2)',
                  borderRadius:8, padding:'10px 14px', minWidth:0 }}>
      <div style={{ fontSize:10, color:'var(--ink2)', fontWeight:600,
                    textTransform:'uppercase', letterSpacing:'0.05em',
                    marginBottom:4 }}>
        {icon} {label}
      </div>
      <div style={{ fontSize:15, fontWeight:700, color:'var(--indigo)' }}>
        {value}
        {badge && (
          <span style={{ fontSize:9, fontWeight:600, marginLeft:6,
                         background: badgeColor+'22', color: badgeColor,
                         border:`1px solid ${badgeColor}44`,
                         borderRadius:3, padding:'1px 5px' }}>
            {badge}
          </span>
        )}
      </div>
      {sub && (
        <div style={{ fontSize:10, color:'var(--ink2)', marginTop:2 }}>{sub}</div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function PanchangTab({ chart }: Props) {
  if (!chart?.planets) return (
    <div style={{ padding:32, textAlign:'center', color:'var(--ink2)' }}>
      Chart data not available.
    </div>
  );

  const pt = chart?.panchang_times ?? {};
  const { vara, tithiNum, tithiIdx, paksha, nakNum, yogaNum, karanaName }
    = computePanchang(chart);

  const tithiName   = TITHI_NAMES[tithiIdx];
  const tithiGood   = TITHI_GOOD.has(tithiNum)
                      ? 'Auspicious' : TITHI_BAD.has(tithiNum)
                      ? 'Inauspicious' : 'Neutral';
  const yogaGood    = YOGA_BAD.has(yogaNum) ? 'Inauspicious' : 'Auspicious';
  const karanaGood  = KARANA_BAD.has(karanaName) ? 'Inauspicious' : 'Auspicious';
  const ghata       = ghataForVara(vara);
  const isGhata     = ghata.includes(nakNum);
  const moonNakName = NAK_NAMES[nakNum];

  const badgeCol = (qual: string) =>
    qual === 'Auspicious' ? '#0A6E5C' : qual === 'Inauspicious' ? '#7A1C1C' : '#C8920A';

  return (
    <div style={{ padding:'10px 0' }}>

      {/* ── 5 Panchang Limbs ────────────────────────────────────────────── */}
      <div style={{ background:'#2E2560', color:'#F4C430', fontWeight:700,
                    fontSize:12, padding:'6px 14px', borderRadius:'8px 8px 0 0',
                    letterSpacing:'0.05em' }}>
        PANCHA ANGA — 5 LIMBS OF BIRTH MOMENT
      </div>
      <div style={{ border:'1px solid var(--parch2)', borderTop:'none',
                    borderRadius:'0 0 8px 8px', padding:12, marginBottom:16,
                    background:'var(--cream)' }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:8 }}>

          <LimbCard icon="📅" label="Vara (Weekday)"
            value={VARA_NAMES[vara]}
            sub={`Lord: ${VARA_LORDS[vara]} · ${VARA_NATURE[vara]}`}
            badge={VARA_NATURE[vara]}
            badgeColor={VARA_NATURE[vara]==='Auspicious'?'#0A6E5C':
                        VARA_NATURE[vara]==='Fierce'?'#7A1C1C':'#C8920A'} />

          <LimbCard icon="🌙" label={`Tithi (Day ${tithiNum})`}
            value={tithiName}
            sub={paksha}
            badge={tithiGood}
            badgeColor={badgeCol(tithiGood)} />

          <LimbCard icon="⭐" label={`Nakshatra (${nakNum}/27)`}
            value={moonNakName}
            sub={`Lord: ${NAK_LORDS[nakNum]} · Pada ${
              Math.floor(((chart?.planets?.CHANDRA?.degree ?? 0) % (360/27))
                          / (360/27/4)) + 1}`}
            badge={isGhata ? '⚠ Ghata' : undefined}
            badgeColor="#7A1C1C" />

          <LimbCard icon="🔯" label={`Yoga (${yogaNum}/27)`}
            value={YOGA_NAMES[yogaNum]}
            sub="Sun+Moon longitude sum"
            badge={yogaGood}
            badgeColor={badgeCol(yogaGood)} />

          <LimbCard icon="☯" label="Karana (Half-Tithi)"
            value={karanaName}
            sub="Half-day period"
            badge={karanaGood}
            badgeColor={badgeCol(karanaGood)} />
        </div>
      </div>

      {/* ── Timing Details ──────────────────────────────────────────────── */}
      <div style={{ background:'#2E2560', color:'#F4C430', fontWeight:700,
                    fontSize:12, padding:'6px 14px', borderRadius:'8px 8px 0 0',
                    letterSpacing:'0.05em' }}>
        BIRTH DAY TIMINGS (IST)
      </div>
      <div style={{ border:'1px solid var(--parch2)', borderTop:'none',
                    borderRadius:'0 0 8px 8px', padding:12, marginBottom:16,
                    background:'var(--cream)' }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
          {[
            ['🌅 Sunrise', pt.sunrise_ist ?? '—', ''],
            ['🌇 Sunset',  pt.sunset_ist  ?? '—', ''],
            ['☀️ Day Duration',
              pt.day_duration_hrs ? `${Number(pt.day_duration_hrs).toFixed(2)} hrs` : '—',
              pt.is_day_birth !== undefined
                ? (pt.is_day_birth ? 'Day birth' : 'Night birth') : ''],
            ['🕐 Hora Lord', pt.hora_lord ?? '—',
              'Planetary hour at birth'],
          ].map(([label, val, sub]) => (
            <LimbCard key={label as string}
              icon="" label={label as string} value={val as string}
              sub={sub as string || undefined} />
          ))}
        </div>
      </div>

      {/* ── Ghata Chakra ───────────────────────────────────────────────── */}
      <div style={{ background:'#2E2560', color:'#F4C430', fontWeight:700,
                    fontSize:12, padding:'6px 14px', borderRadius:'8px 8px 0 0',
                    letterSpacing:'0.05em' }}>
        GHATA CHAKRA — INAUSPICIOUS NAKSHATRAS BY WEEKDAY
      </div>
      <div style={{ border:'1px solid var(--parch2)', borderTop:'none',
                    borderRadius:'0 0 8px 8px', padding:12, marginBottom:16,
                    background:'var(--cream)' }}>

        {/* Birth alert if Ghata */}
        {isGhata && (
          <div style={{ background:'#FFF0F0', border:'1px solid #7A1C1C',
                        borderRadius:6, padding:'8px 12px', marginBottom:12,
                        fontSize:12, color:'#7A1C1C' }}>
            ⚠️ Birth Nakshatra <strong>{moonNakName}</strong> is a Ghata nakshatra
            for <strong>{VARA_NAMES[vara]}</strong>. Traditional interpretation suggests
            consulting a Jyotishi for remedial guidance.
          </div>
        )}
        {!isGhata && (
          <div style={{ background:'#E0F5F0', border:'1px solid #0A6E5C',
                        borderRadius:6, padding:'8px 12px', marginBottom:12,
                        fontSize:12, color:'#0A6E5C' }}>
            ✓ Birth Nakshatra <strong>{moonNakName}</strong> is NOT in the Ghata
            for <strong>{VARA_NAMES[vara]}</strong>.
          </div>
        )}

        {/* 7-weekday grid */}
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
            <thead>
              <tr style={{ background:'var(--sfll)' }}>
                <th style={{ padding:'6px 10px', textAlign:'left',
                             color:'var(--indigo)', fontWeight:700, fontSize:10,
                             borderBottom:'1px solid var(--parch2)' }}>
                  Weekday
                </th>
                {[1,2,3].map(n => (
                  <th key={n} style={{ padding:'6px 10px', textAlign:'center',
                                       color:'var(--indigo)', fontWeight:700, fontSize:10,
                                       borderBottom:'1px solid var(--parch2)' }}>
                    Ghata Nak {n}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {VARA_NAMES.map((vname, vi) => {
                const g = ghataForVara(vi);
                const isThisVara = vi === vara;
                return (
                  <tr key={vi} style={{
                    background: isThisVara ? '#FFF8EC' : vi%2===0 ? 'var(--ivory)':'var(--cream)',
                    borderBottom:'1px solid var(--parch2)',
                  }}>
                    <td style={{ padding:'7px 10px', fontWeight: isThisVara ? 700 : 500,
                                 color: isThisVara ? '#C8920A' : 'var(--indigo)' }}>
                      {vname}
                      {isThisVara && (
                        <span style={{ fontSize:9, marginLeft:5, background:'#F4C430',
                                       color:'#2E2560', borderRadius:3,
                                       padding:'0 4px', fontWeight:700 }}>
                          BIRTH
                        </span>
                      )}
                    </td>
                    {g.map((nk, i) => {
                      const isBirthNak = nk === nakNum;
                      return (
                        <td key={i} style={{ padding:'7px 10px', textAlign:'center' }}>
                          <span style={{
                            display:'inline-block',
                            background: isBirthNak ? '#7A1C1C' : '#FFF0F0',
                            color: isBirthNak ? '#fff' : '#7A1C1C',
                            border:'1px solid #7A1C1C44',
                            borderRadius:5, padding:'2px 8px',
                            fontWeight: isBirthNak ? 700 : 500, fontSize:11,
                          }}>
                            {NAK_NAMES[nk]}
                            <span style={{ fontSize:9, marginLeft:3, opacity:0.7 }}>
                              ({nk})
                            </span>
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop:8, fontSize:10, color:'var(--ink3)',
                      fontStyle:'italic' }}>
          Ghata Chakra per Muhurta Chintamani · Formula: starting nak +5 per weekday,
          +9 within each day · 3 Ghata nakshatras per weekday out of 27.
          Birth weekday row highlighted in gold.
        </div>
      </div>

      {/* ── Yoga Reference ─────────────────────────────────────────────── */}
      <details style={{ marginTop:8 }}>
        <summary style={{ cursor:'pointer', fontSize:11, color:'var(--ink2)',
                          padding:'4px 0', fontWeight:600 }}>
          ▸ 27 Yoga Reference (click to expand)
        </summary>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)',
                      gap:4, marginTop:8 }}>
          {YOGA_NAMES.slice(1).map((name, i) => {
            const num = i + 1;
            const bad = YOGA_BAD.has(num);
            return (
              <div key={num} style={{
                padding:'3px 8px', borderRadius:4, fontSize:10,
                background: bad ? '#FFF0F0' : '#E0F5F0',
                color: bad ? '#7A1C1C' : '#0A6E5C',
                border: `1px solid ${bad?'#7A1C1C':'#0A6E5C'}33`,
                fontWeight: num === yogaNum ? 700 : 400,
                outline: num === yogaNum ? `2px solid ${bad?'#7A1C1C':'#0A6E5C'}` : 'none',
              }}>
                {num}. {name} {bad ? '✗' : '✓'}
                {num === yogaNum && ' ◀ Birth'}
              </div>
            );
          })}
        </div>
      </details>

    </div>
  );
}
